import { getApiAddress } from '@/core/apiConfig';
import type {
  CollectionDto,
  CollectionsSummaryDto,
  CreateCollectionDto,
  CreateMockupDto,
  StatsResponse,
  ModerateCommentRequestDto,
  ModerateCommentResponseDto,
  LogoMoveRequestDto,
  LogoMoveResponseDto,
  LogoSignedUrl,
  MockupDto,
  MockupSummaryDto,
  PreviewMoveRequestDto,
  PreviewMoveResponseDto,
  UpdateCollectionDto,
  UpdateMockupDto,
} from './mockupsDto';
import type {
  CollectionLogosUpdateDto,
  CollectionLogosUpdateResultDto,
} from '@/pages/ManageGraphics/ManageGraphics.types';

export const DECORATOR_MOCKUPS_ENDPOINT = 'decorator-mockups/v1';

export class MockupsApi {
  static async getMockupsSummary(
    page = 1,
    pageSize = 100,
    type?: 'myWork' | 'sharedWithMe' | 'orgShared',
    sort = '-updatedAt',
    search?: string,
    orgId?: string,
  ): Promise<MockupSummaryDto[]> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sort,
    });

    if (type) {
      params.append('type', type);
    }

    if (orgId) {
      params.append('org', orgId);
    }

    if (search) {
      params.append('search', search);
    }

    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/summary?${params.toString()}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async duplicateMockup(id: string): Promise<MockupDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}/duplicate`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async getStats(): Promise<StatsResponse | undefined> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/stats`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async getMockup(id: string): Promise<MockupDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async createMockup(mockup: CreateMockupDto): Promise<MockupDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(mockup),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async deleteMockup(id: string): Promise<boolean> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'DELETE',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  static async updateMockup(id: string, body: UpdateMockupDto): Promise<MockupDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async setMockupPublic(id: string, isPublic: boolean): Promise<{ isPublic: boolean }> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}/public`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: isPublic ? 'PUT' : 'DELETE',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return status === 204 ? { isPublic } : await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async makePreview(id: string): Promise<void> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/${id}/make-preview`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }
    } catch (err) {
      console.error(err);
    }
  }

  static async getCollectionsSummary(
    page = 1,
    pageSize = 100,
    type?: 'myWork' | 'sharedWithMe' | 'orgShared',
    sort = '-updatedAt',
    search?: string,
    orgId?: string,
  ): Promise<CollectionsSummaryDto[]> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      product_limit: '8',
      sort,
    });

    if (type) {
      params.append('type', type);
    }

    if (orgId) {
      params.append('org', orgId);
    }

    if (search) {
      params.append('search', search);
    }

    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/overview?${params.toString()}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async getCollection(
    id: string,
    liveblocksData?: boolean,
    page?: number,
    pageSize?: number,
    sort?: string,
  ): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = new URL(`${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/${id}`);
    if (liveblocksData) {
      url.searchParams.append('liveblocksData', 'true');
    }
    if (page !== undefined) {
      url.searchParams.append('page', page.toString());
    }
    if (pageSize !== undefined) {
      url.searchParams.append('page_size', pageSize.toString());
    }
    if (sort) {
      url.searchParams.append('sort', sort);
    }

    try {
      const response = await fetch(url.toString(), {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async getDraftsCollection(): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/drafts`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async setCollectionsOrgSharing(
    identityOrgId: string,
    enabled: boolean,
  ): Promise<void> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/org-sharing`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'PUT',
        headers: myHeaders,
        body: JSON.stringify({ enabled, identityOrgId }),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  static async createCollection(collection: CreateCollectionDto): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(collection),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async deleteCollection(id: string): Promise<boolean> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/${id}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'DELETE',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  static async updateCollection(id: string, body: UpdateCollectionDto): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/${id}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async setCollectionPublic(id: string, isPublic: boolean): Promise<{ isPublic: boolean }> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/${id}/public`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: isPublic ? 'PUT' : 'DELETE',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return status === 204 ? { isPublic } : await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async duplicateCollection(id: string): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/collections/${id}/duplicate`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async getLogoSignedUrl(path: string): Promise<LogoSignedUrl> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/logos/signedUrl/${encodeURIComponent(
      path,
    )}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: myHeaders,
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async moveLogo(body: LogoMoveRequestDto): Promise<LogoMoveResponseDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/logos/moveToLogosFolder`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async movePreview(body: PreviewMoveRequestDto): Promise<PreviewMoveResponseDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/previews/moveToPreviewsFolder`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
    }
  }

  static async createBatch(body: {
    name: string;
    customer: string;
    originalMockupId: string;
    styles: {
      id: string;
      productName: string;
    }[];
    identityOrgId: string;
  }): Promise<CollectionDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/batch/collection-mockups`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  static async moderateComment(
    body: ModerateCommentRequestDto,
  ): Promise<ModerateCommentResponseDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/moderate-comment`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  static async addLogoMd5(body: {
    dryRun: boolean;
    batchSize: number;
    onlyMissing: boolean;
    collectionId: string;
  }): Promise<void> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/mockups/admin/add-logo-md5`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  static async updateCollectionLogos(
    body: CollectionLogosUpdateDto,
  ): Promise<CollectionLogosUpdateResultDto> {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const url = `${getApiAddress()}/${DECORATOR_MOCKUPS_ENDPOINT}/batch/collection-logos-update`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      const { status } = response;

      if (status < 200 || status >= 400) {
        throw new Error(status.toString());
      }

      return await response.json();
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
}
